import React, { useContext, useEffect, useState } from 'react';

import { useI18n } from '@braincube/i18n';

import { SsoContext, startFetching, stopFetching } from 'App/contexts';
import { MainSection, MainSectionContent, MainSectionTitle } from 'components/MainSection';
import Explanation from 'components/Explanation';

const PORTAL_ENDPOINT = 'https://api.federation.mybraincube.com/portal/generate_jwt';

function Portal() {
    const i18n = useI18n();
    const { dispatch } = useContext(SsoContext);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        const generatePortalJwt = async () => {
            dispatch(startFetching());

            try {
                // Step 1: Request JWT generation from SSO backend
                const jwtResponse = await fetch(
                    `https://${window.BC_API_ENDPOINTS_CONF.sso}/sso-server/ws/session/jwt_portal`,
                    {
                        credentials: 'include',
                        method: 'POST',
                        body: '',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    },
                );

                // If not authenticated, redirect to login page with portal redirect
                if (jwtResponse.status === 401) {
                    const params = new URLSearchParams(window.location.search);
                    const prefillEmail = params.get('prefill_email');
                    const redirectUrl = new URL('/', window.location.origin);
                    if (prefillEmail) {
                        redirectUrl.searchParams.set('prefill_email', prefillEmail);
                    }
                    redirectUrl.searchParams.set('redirect', '/portal');
                    window.location.assign(redirectUrl.toString());
                    return;
                }

                if (!jwtResponse.ok) {
                    throw new Error(`Failed to generate portal JWT: ${jwtResponse.status}`);
                }

                const jwt = await jwtResponse.text();

                if (!jwt) {
                    throw new Error('No JWT received from backend');
                }

                // Step 2: Post JWT to portal backend to generate scoped cookie
                const portalResponse = await fetch(PORTAL_ENDPOINT, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ token: jwt }),
                });

                if (!portalResponse.ok) {
                    throw new Error(`Portal endpoint returned ${portalResponse.status}`);
                }

                await portalResponse.json();

                // Step 3: Redirect to portal or the URL provided in response
                dispatch(stopFetching());
                const currentQueryParams = new URLSearchParams(window.location.search);
                const redirectUrl = (PORTAL_ENDPOINT.split('/portal/')[0]).replace('api.', 'portal.') + '?' + currentQueryParams.toString();
                window.location.assign(redirectUrl);
            } catch (error) {
                console.error('Portal JWT generation failed:', error);
                dispatch(stopFetching());
                setErrorMessage(i18n.tc('portal.error'));
            }
        };

        generatePortalJwt();
    }, [dispatch, i18n]);

    if (errorMessage) {
        return (
            <MainSection>
                <MainSectionTitle>{i18n.t('portal.title')}</MainSectionTitle>
                <Explanation>{errorMessage}</Explanation>
            </MainSection>
        );
    }

    return (
        <MainSection>
            <MainSectionTitle>{i18n.t('portal.title')}</MainSectionTitle>
            <MainSectionContent>{i18n.tc('portal.text')}</MainSectionContent>
        </MainSection>
    );
}

export default Portal;
